 
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Mail, Calendar, CheckCircle2, PlayCircle, Activity, Info } from "lucide-react";

import { Link } from "react-router-dom";

const LapsleeveGastrectomy = () => {
  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20">
      
      {/* Hero Section */}
      <section className="relative bg-[#800000] text-white py-20 px-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5 skew-x-12 transform translate-x-20" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-1.5 rounded-full mb-6 border border-white/30">
            <Activity className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">
              Big Bariatrics
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            Laparoscopic Sleeve Gastrectomy
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 -mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Sidebar */}
        <aside className="lg:col-span-3 space-y-6">
          <Card className="sticky top-6 shadow-xl border-none bg-white/90 backdrop-blur-md">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-lg font-bold text-[#800000] flex items-center gap-2">
                <Info className="w-5 h-5 text-[#800000]" /> Patient Portal
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              <div className="mt-4 p-4 bg-[#800000] rounded-2xl text-white shadow-lg">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> For Appointment
                </h4>
                <p className="text-xs text-white/80 mb-4">
                  Schedule your surgical consultation today.
                </p>
                <Link to="/contact">
                <Button className="w-full bg-white text-[#800000] hover:bg-gray-100 font-bold mt-2">
                  Reserve Now
                </Button>
                </Link>
                <div className="mt-4 pt-4 border-t border-white/20 space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Phone className="w-3 h-3" /> +91-755-4055123
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-3 h-3" /> contact@bigbariatrics.com
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="lg:col-span-9 space-y-12">

          {/* Procedure Section */}
          <section className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1 space-y-6 text-gray-700">
                <p className="text-xl leading-relaxed">
                  Laparoscopic Sleeve Gastrectomy (LSG) is a widely performed bariatric surgery in which approximately 75–80% of the stomach is permanently removed, leaving a narrow “sleeve-shaped” stomach.
                </p>
                <p className="text-lg leading-relaxed">
                  The procedure is performed using small keyhole incisions (laparoscopic technique), resulting in reduced pain, faster recovery, and minimal scarring compared to traditional open surgery.
                </p>
                <div className="w-full bg-gray-100 rounded-2xl overflow-hidden shadow-inner">
                  <img 
                    src="/lap-01.jpg" 
                    alt="LSG Procedure"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="bg-[#ffe5e5] border-l-4 border-[#800000] p-4 italic rounded-r-lg">
                  Unlike purely restrictive procedures, LSG also has a hormonal effect by reducing hunger hormones (ghrelin), helping patients feel less hungry and achieve sustained weight loss.
                </p>

                {/* Benefits */}
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Key Benefits
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>Significant and sustained weight loss</li>
                    <li>Reduction in hunger due to hormonal changes</li>
                    <li>Improvement in diabetes, BP, and other conditions</li>
                    <li>Minimally invasive with small incisions</li>
                  </ul>
                </div>

                {/* Who is it for */}
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Who is it for?
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>Patients with moderate to severe obesity</li>
                    <li>Individuals with obesity-related conditions</li>
                    <li>Patients seeking long-term weight loss solution</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Recovery Section */}
          <section>
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Your Recovery Journey
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 rounded-3xl overflow-hidden shadow-lg border border-gray-100">
              <div className="bg-white p-8 text-center">
                <div className="w-12 h-12 bg-[#ffe5e5] text-[#800000] rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  01
                </div>
                <h4 className="font-bold mb-2">Week 1</h4>
                <p className="text-sm text-gray-600">Liquid diet and recovery</p>
              </div>
              <div className="bg-[#ffe5e5] p-8 text-center">
                <div className="w-12 h-12 bg-[#800000] text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  02
                </div>
                <h4 className="font-bold mb-2">Weeks 2-4</h4>
                <p className="text-sm text-gray-600">Soft & pureed foods</p>
              </div>
              <div className="bg-white p-8 text-center">
                <div className="w-12 h-12 bg-[#ffe5e5] text-[#800000] rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  03
                </div>
                <h4 className="font-bold mb-2">Month 2+</h4>
                <p className="text-sm text-gray-600">Healthy long-term diet</p>
              </div>
            </div>
          </section>

          {/* Video Section */}
          {/* <section className="bg-[#800000] rounded-3xl p-10 text-white">
            <h3 className="text-2xl font-bold mb-6">Educational Videos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2].map((i) => (
                <div key={i} className="relative aspect-video bg-[#660000] rounded-xl flex items-center justify-center">
                  <PlayCircle className="w-14 h-14 text-white/70" />
                </div>
              ))}
            </div>
          </section> */}

          {/* Checklist */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-emerald-50 border-emerald-100">
              <CardHeader>
                <CardTitle className="text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> Pre-Op Checklist
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p>Complete medical evaluation</p>
                <p>Pre-operative diet plan</p>
                <p>Smoking & alcohol cessation</p>
              </CardContent>
            </Card>

            <Card className="bg-orange-50 border-orange-100">
              <CardHeader>
                <CardTitle className="text-orange-800 flex items-center gap-2">
                  <Info className="w-5 h-5" /> Expert Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                Our team provides continuous post-surgery care, dietary guidance, and long-term follow-up to ensure successful weight loss outcomes.
              </CardContent>
            </Card>
          </div>

        </main>
      </div>
    </div>
  );
};

export default LapsleeveGastrectomy;